import { useAuth } from "@/hooks/use-auth-simple";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Lightbulb, ArrowLeft, ThumbsUp, ThumbsDown, Plus, Filter, Search } from "lucide-react";
import { Link, Redirect } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";

type FeatureRequest = {
  id: string;
  userId: string;
  title: string;
  description: string;
  category: string | null;
  status: string;
  upvoteCount: number;
  downvoteCount: number;
  createdAt: string;
  userVote?: 'up' | 'down' | null;
  user?: {
    firstName: string | null;
    lastName: string | null;
    username: string;
  };
};

export default function FeatureRequestsPage() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("votes");
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newCategory, setNewCategory] = useState<string>("");

  // Fetch feature requests
  const { data: featureRequests, isLoading } = useQuery<FeatureRequest[]>({
    queryKey: ['/api/feature-requests'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/feature-requests');
      return await res.json();
    },
  });

  // Submit feature request
  const submitMutation = useMutation({
    mutationFn: async (data: { title: string; description: string; category: string }) => {
      const res = await apiRequest('POST', '/api/feature-requests', data);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/feature-requests'] });
      setSubmitDialogOpen(false);
      setNewTitle("");
      setNewDescription("");
      setNewCategory("");
      toast({
        title: "Feature Request Submitted",
        description: "Your idea has been submitted successfully!",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Submission Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Vote on feature request
  const voteMutation = useMutation({
    mutationFn: async ({ featureId, voteType }: { featureId: string; voteType: 'up' | 'down' }) => {
      const res = await apiRequest('POST', `/api/feature-requests/${featureId}/vote`, { voteType });
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/feature-requests'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Vote Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleVote = (featureId: string, voteType: 'up' | 'down') => {
    voteMutation.mutate({ featureId, voteType });
  };

  const handleSubmit = () => {
    if (!newTitle.trim() || !newDescription.trim()) {
      toast({
        title: "Validation Error",
        description: "Title and description are required.",
        variant: "destructive",
      });
      return;
    }
    submitMutation.mutate({
      title: newTitle,
      description: newDescription,
      category: newCategory || null,
    });
  };

  // Filter and sort feature requests
  const filteredRequests = featureRequests
    ?.filter((req) => {
      const matchesSearch = req.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || req.status === statusFilter;
      const matchesCategory = categoryFilter === "all" || req.category === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    })
    .sort((a, b) => {
      if (sortBy === "votes") {
        return (b.upvoteCount - b.downvoteCount) - (a.upvoteCount - a.downvoteCount);
      } else if (sortBy === "newest") {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      } else {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
    }) || [];

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#1a1a1a]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-100"></div>
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/auth" />;
  }

  return (
    <div 
      className="min-h-screen bg-[#1a1a1a] text-gray-100" 
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)'
      }}
    >
      {/* Header */}
      <header 
        className="px-4 sm:px-6 h-14 border-b border-[#3a3a3a] bg-[#2a2a2a] sticky top-0 z-50"
        style={{ 
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          height: 'calc(56px + env(safe-area-inset-top, 0px))',
          paddingLeft: 'calc(16px + env(safe-area-inset-left, 0px))',
          paddingRight: 'calc(16px + env(safe-area-inset-right, 0px))'
        }}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center h-full">
          <div className="flex items-center gap-4" style={{ textAlign: 'left' }}>
            <Link href="/">
              <Button 
                variant="ghost" 
                size="sm" 
                className="transition-all duration-200 rounded-md px-3 text-gray-300 hover:text-gray-100 hover:bg-[#3a3a3a] border border-[#3a3a3a] touch-manipulation"
                style={{ minHeight: '44px', fontSize: '16px', textAlign: 'center' }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Back to Chat</span>
                <span className="sm:hidden">Back</span>
              </Button>
            </Link>
            <div className="border-l border-[#3a3a3a] pl-4 h-10 flex items-center">
              <div className="flex items-center gap-2 text-lg font-semibold text-gray-100" style={{ textAlign: 'left' }}>
                <Lightbulb className="h-5 w-5 text-yellow-400" />
                <span style={{ textAlign: 'left' }}>Feature Requests</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" style={{ textAlign: 'left' }}>
        {/* Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search feature requests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-[#2a2a2a] border-[#3a3a3a] text-gray-100 pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-[#2a2a2a] border-[#3a3a3a] text-gray-100 w-full sm:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#2a2a2a] border-[#3a3a3a]">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="under-review">Under Review</SelectItem>
                <SelectItem value="planned">Planned</SelectItem>
                <SelectItem value="in-development">In Development</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="bg-[#2a2a2a] border-[#3a3a3a] text-gray-100 w-full sm:w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-[#2a2a2a] border-[#3a3a3a]">
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="UI/UX">UI/UX</SelectItem>
                <SelectItem value="Functionality">Functionality</SelectItem>
                <SelectItem value="Performance">Performance</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="bg-[#2a2a2a] border-[#3a3a3a] text-gray-100 w-full sm:w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-[#2a2a2a] border-[#3a3a3a]">
                <SelectItem value="votes">Most Votes</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Feature Requests List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-100"></div>
          </div>
        ) : filteredRequests.length === 0 ? (
          <Card className="border-[#3a3a3a] bg-[#2a2a2a] p-12 text-center flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center justify-center">
              <Lightbulb className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-300 mb-2">No Feature Requests</h3>
              <p className="text-gray-500 mb-6">Be the first to submit an idea!</p>
              <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#3a3a3a] hover:bg-[#4a4a4a] text-gray-100 border border-[#4a4a4a]">
                    <Plus className="h-4 w-4 mr-2" />
                    Submit Idea
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#2a2a2a] border-[#3a3a3a] text-gray-100">
                  <DialogHeader>
                    <DialogTitle>Submit a Feature Request</DialogTitle>
                    <DialogDescription className="text-gray-400">
                      Share your idea with the team. The best ideas win!
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-300 mb-2 block">Title</label>
                      <Input
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        placeholder="Brief title for your idea"
                        className="bg-[#1a1a1a] border-[#3a3a3a] text-gray-100"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300 mb-2 block">Description</label>
                      <Textarea
                        value={newDescription}
                        onChange={(e) => setNewDescription(e.target.value)}
                        placeholder="Describe your idea in detail..."
                        className="bg-[#1a1a1a] border-[#3a3a3a] text-gray-100 min-h-[120px]"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300 mb-2 block">Category</label>
                      <Select value={newCategory} onValueChange={setNewCategory}>
                        <SelectTrigger className="bg-[#1a1a1a] border-[#3a3a3a] text-gray-100">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#2a2a2a] border-[#3a3a3a]">
                          <SelectItem value="UI/UX">UI/UX</SelectItem>
                          <SelectItem value="Functionality">Functionality</SelectItem>
                          <SelectItem value="Performance">Performance</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setSubmitDialogOpen(false)}
                      className="border-[#3a3a3a] text-gray-300 hover:bg-[#3a3a3a]"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={submitMutation.isPending}
                      className="bg-[#4a4a4a] hover:bg-[#5a5a5a] text-gray-100"
                    >
                      {submitMutation.isPending ? "Submitting..." : "Submit"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request) => (
              <Card key={request.id} className="border-[#3a3a3a] bg-[#2a2a2a] hover:border-[#4a4a4a] transition-colors">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-gray-100 text-lg mb-2">{request.title}</CardTitle>
                      <div className="flex items-center gap-2 mb-2">
                        {request.category && (
                          <Badge className="bg-[#3a3a3a] text-gray-300 border-[#4a4a4a] text-xs">
                            {request.category}
                          </Badge>
                        )}
                        <Badge className={`text-xs ${
                          request.status === 'open' ? 'bg-gray-100 text-gray-800 border-gray-200' :
                          request.status === 'planned' ? 'bg-gray-200 text-gray-800 border-gray-300' :
                          request.status === 'in-development' ? 'bg-gray-300 text-gray-900 border-gray-400' :
                          request.status === 'completed' ? 'bg-gray-400 text-gray-900 border-gray-500' :
                          'bg-gray-100 text-gray-800 border-gray-200'
                        }`}>
                          {request.status.replace('-', ' ')}
                        </Badge>
                      </div>
                      <CardDescription className="text-gray-400 mt-2">
                        {request.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => handleVote(request.id, 'up')}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors ${
                          request.userVote === 'up'
                            ? 'bg-green-600 text-white'
                            : 'bg-[#3a3a3a] text-gray-300 hover:bg-[#4a4a4a]'
                        }`}
                      >
                        <ThumbsUp className="h-4 w-4" />
                        <span>{request.upvoteCount}</span>
                      </button>
                      <button
                        onClick={() => handleVote(request.id, 'down')}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors ${
                          request.userVote === 'down'
                            ? 'bg-red-600 text-white'
                            : 'bg-[#3a3a3a] text-gray-300 hover:bg-[#4a4a4a]'
                        }`}
                      >
                        <ThumbsDown className="h-4 w-4" />
                        <span>{request.downvoteCount}</span>
                      </button>
                    </div>
                    <div className="text-sm text-gray-500">
                      {request.user && (
                        <span>
                          {request.user.firstName || request.user.lastName
                            ? `${request.user.firstName || ''} ${request.user.lastName || ''}`.trim()
                            : request.user.username}
                        </span>
                      )}
                      <span className="ml-2">
                        {format(new Date(request.createdAt), 'MMM d, yyyy')}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

